import {
  Identify,
  identify,
  init,
  reset,
  track,
  setGroup,
  setUserId,
} from "@amplitude/analytics-browser"

import { localStorageItems } from "./storage"
import { isVercelDeployment } from "../constants"

export const ANALYTIC_EVENTS = {
  //// Auth
  PARENT_LOGIN_APPROVED: "parent_login_approved", // Parent successfully logs in with email/password
  GOOGLE_LOGIN_APPROVED: "google_login_approved", // Parent successfully logs in with Google
  APPLE_LOGIN_APPROVED: "apple_login_approved", // Parent successfully logs in with Apple
  AUTH_NO_ACCOUNT: "auth_no_account", // User attempts login but has no account
  AUTH_HAS_ACCOUNT: "auth_has_account", // User attempts login and has existing account
  CREATE_LOGIN: "create_login", // User creates a new login/account
  PURCHASE_COMPLETED: "purchase_completed", // User completes a subscription purchase
  PURCHASE_CANCELLED: "purchase_cancelled", // User cancels during purchase flow
  SIGNUP_FAILED: "signup_failed", // Account creation attempt fails

  //// Referrals
  OPEN_REFERRAL_FTUX: "open_referral_ftux", // user sees referral FTUX
  OPEN_REFERRALS_PAGE: "open_referrals_page", // open referral modal ("Give the gift of calm mornings..."), params: { location: "me_tab_icon" | "me_tab_card" }
  SHARE_REFERRAL_GIFT_CARD: "share_referral_gift_card", // when the share sheet is opened, params: { uuid: string }
  SHARE_MORE_CLICK: "share_more_click", // user clicks on "Share more" on modal that tells user how kind they are
  REFERRAL_LANDING_PAGE_VIEWED: "referrals_landing_page_viewed", // user views the referrals landing page at /referral-landing, params: { uuid: string }
  REFERRAL_FEATURE_CAROUSEL_VIEWED: "referral_feature_carousel_viewed", // user scrolls to view the referral preview cards section, params: { uuid: string }
  REFERRAL_TESTIMONIALS_VIEWED: "referral_testimonials_viewed", // user scrolls to view the testimonials section, params: { uuid: string }
  ACTIVATE_GUEST_PASS: "activate_guest_pass", // user clicks "Activate my Guest Pass" at /referral-landing, params: { uuid: string }
  REFERRAL_GIFT_CARD_APPLIED: "referral_gift_card_applied", // the referral gift card is applied to the user after account creation, params: { uuid: string }
  REFERRAL_INVALID_LINK: "referral_invalid_link", // the referral link is invalid, params: { uuid: string }

  //// Invited by therapist/code page
  INVITED_BY_THERAPIST: "invited_by_therapist", // User arrives via therapist invitation
  NOT_INVITED_BY_THERAPIST: "not_invited_by_therapist", // User arrives organically (not via therapist)
  ONBOARDING_USING_THERAPIST_CODE: "onboarding_using_therapist_code", // User enters therapist code during onboarding

  //// Onboarding
  SHOW_WELCOME: "show_welcome", // Welcome screen is displayed to user
  START_ONBOARD: "start_onboard", // User begins onboarding process
  START_CHILD_ONBOARDING_SURVEY: "start_child_onboarding_survey", // User starts child-specific survey
  INPUT_CHILD_INFO_INTENT: "input_child_info_intent", // User begins entering child information
  INPUT_CHILD_INFORMATION: "input_child_information", // User completes child information entry, params: { gender: string | null, birthdate: string | null }
  ADD_CHILDREN: "add_children", // User adds one or more children to account
  GET_STARTED_FOR_FREE: "get_started_for_free", // User clicks “Get started for Free” on onboarding custom plan section
  SELECT_PARENT_AVATAR: "select_parent_avatar", // Parent chooses their avatar
  ADD_PARENT_NAME: "add_parent_name", // Parent enters their name
  ADD_PARENT_NICKNAME: "add_parent_nickname", // Parent enters their nickname
  ENTER_COPARENT_CODE: "enter_coparent_code", // User enters code to link with coparent
  INVALID_COPARENT_CODE: "invalid_coparent_code", // Entered coparent code is invalid
  ONBOARDING_QUESTION: (question: number) => `onboarding_question_${question}`, // User answers specific onboarding question, params: { answers: number[] | string }
  SHOW_CHECKOUT: "show_checkout", // Checkout screen is displayed
  CHECKOUT_ERROR: "checkout_error", // Stripe confirmation error, params: { error_code, decline_code }
  SHOW_BILLING: "show_billing", // Billing screen is displayed
  EXPERIMENT_WEB_WELCOME: "experiment_web_welcome", // A/B test welcome screen variant shown
  EXPERIMENT_WEB_WELCOME_CTA: "experiment_web_welcome_cta", // User clicks CTA in welcome experiment
  EXPERIMENT_WEB_ONBOARDING_CUSTOM_PLAN:
    "experiment_web_onboarding_custom_plan", // User is cohorted into the onboarding custom plan experiment, params: { cohort: "control" | "variant-a" }
  SHOW_PREPAYWALL_CREDIBILITY_PROOF: "show_prepaywall_credibility_proof", // Credibility-proof screen shown (variant-a only). Note: cohort assignment events for backend-driven experiments fire automatically as `experiment_<name>` from getExperimentCohorts() in util/experiments.ts — no separate constant needed here.
  MOBILE_APP_DOWNLOAD_PROMPT_SEEN: "mobile_app_download_prompt_seen", // User sees mobile app download prompt
  MOBILE_APP_DOWNLOAD_BANNER_CLICK: "mobile_app_download_banner_click", // User clicks mobile app download banner
  HAS_ACCOUNT: "has_account", // User indicates they have existing account
  HAS_FAMILY_CODE: "has_family_code", // User indicates they have a family code
  CONTINUE_ADD_CHILDREN: "continue_add_children", // User proceeds to add children
  ONBOARD_HAS_ACCOUNT_LOGIN_BUTTON_CLICK:
    "onboard_has_account_login_button_click", // User with account clicks login
  ABA_QUESTION_ANSWERED: "aba_question_answered", // user said their child has ABA
  CAMPAIGN_REDIRECT: "campaign_redirect", // user is directed to app via campaign link
  FORCE_ONBOARD_INTRO_ALERT: "force_onboard_intro_alert", // when user clicks "Next" on Welcome modal after signing up
  SHOW_FORCE_ONBOARD: "show_force_onboard", // when child set up ftux modal is shown
  OPEN_DOWNLOAD_INSTRUCTIONS: "open_download_instructions", // when the user clicks start on a child in child onboarding ftux
  FORCE_ONBOARD_LATER: "force_onboard_later", // when they skip the child onboarding ftux

  //// Coach Onboarding
  COACH_ONBOARDING_QUESTIONNAIRE_START: "coach_onboarding_questionnaire_start", // user clicks "Continue" on coach onboarding disclaimer
  COACH_ONBOARDING_CHILD_INFO_START: "coach_onboarding_child_info_start", // user clicks "Continue with child info" on coach onboarding add children start
  COACH_ONBOARDING_SIGNUP: "coach_onboarding_signup", // user clicks "Continue" on coach onboarding signup
  COACH_ONBOARDING_CHOICE_TAKE_QUIZ: "coach_onboarding_choice_take_quiz", // parent picks "take the quiz" on the new QUIZ_OR_SKIP intro screen
  COACH_ONBOARDING_CHOICE_SKIP_QUIZ: "coach_onboarding_choice_skip_quiz", // parent picks "skip the quiz" on the new QUIZ_OR_SKIP intro screen → routed to minimal sign-up + default-filled questionnaire
  COMPLETED_QUIZ: "completed_quiz", // user finishes the wheel-of-life quiz (Stage 2 goals) right before the add-children step. Fires the medium-intent Google Ads conversion (GAEvent.COMPLETED_QUIZ) so we can train bidding on parents who got through the whole quiz but haven't yet committed to entering child info.

  //// Paywall
  SHOW_PREPAYWALL_STATEMENTS: "show_prepaywall_statements", // Pre-paywall screen is displayed
  PRE_PAYWALL_STATEMENT_NO_RESONATES: "pre_paywall_statement_no_resonates", // User clicks "No" on pre-paywall statement. params: { statement: string }
  PRE_PAYWALL_STATEMENT_RESONATES: "pre_paywall_statement_resonates", // User clicks "Yes" on pre-paywall statement. params: { statement: string }
  SHOW_PAYWALL: "show_paywall", // Paywall screen is displayed
  PAYWALL_NEXT: "paywall_next", // User clicks next on paywall, params: { step: number } - 1-indexed on the step the user pressed next on
  PAYWALL_START_TRIAL: "paywall_start_trial", // User starts free trial
  PAYWALL_TOGGLE_FREE_TRIAL: "paywall_toggle_free_trial", // User toggles the free trial on/off on the paywall, params: { enabled: boolean }
  PAYWALL_START_QUARTERLY: "paywall_start_quarterly", // User starts quarterly plan
  PAYWALL_SKIP: "paywall_skip", // User skips paywall
  PAYWALL_JOON_PLAN_SELECT: "paywall_joon_plan_select", // when a joon plan is selected on paywall, params: { plan: "withCoach" | "appOnly" }
  OPEN_QUARTERLY_OFFER: "open_quarterly_offer", // User opens quarterly offer modal
  SHOW_FREE_TRIAL_LIMIT_MODAL: "show_free_trial_limit_modal", // User sees the free trial limit modal
  SHOW_FREE_TRIAL_FIRST_VERIFY_MODAL: "show_free_trial_first_verify_modal", // User sees the free trial first verify modal

  //// Coach In-App Upsell
  COACH_UPSELL_BANNER_VIEWED: "coach_upsell_banner_viewed", // On load of coach upsell banner
  COACH_UPSELL_BANNER_LEARN_MORE: "coach_upsell_banner_learn_more", // User clicks learn more on coach in-app upsell
  COACH_UPSELL_BANNER_DISMISS: "coach_upsell_banner_dismiss", // User clicks dismiss on coach in-app upsell
  COACH_UPSELL_MODAL_VIEWED: "coach_upsell_modal_viewed", // params: { type: "delete" | "reject" }: User views the upsell modal after deleting/rejecting a quest
  COACH_UPSELL_MODAL_LEARN_MORE: "coach_upsell_modal_learn_more", // params: { type: "reject" | "delete" } User clicks learn more on coach in-app upsell
  COACH_UPSELL_MODAL_DISMISS: "coach_upsell_modal_dismiss", // params: { type: "reject" | "delete" } User clicks maybe later on coach in-app upsell
  COACH_VIEW_PLAN_SLIDER: "coach_view_plan_slider", // user views the plan slider { num_sessions: number }

  //// Coaching experiments: every event has the param: {is_tof: boolean} indicating if the user came from TOF or MOF
  MARKETING_TAB_VIEWED: "marketing_tab_viewed", // user views marketing tab
  MARKETING_BUTTON_CLICK: "marketing_button_click", // user clicks "Ready to try it out" or "Learn more" in marketing tab
  MARKETING_COACH_ONBOARDING_START: "marketing_coach_onboarding_start", // landed on coaching/training page, params: { from_popup: boolean, bundle_joonity: boolean }
  MARKETING_CONTINUE_CLICK: "marketing_continue_click", // any time next step is clicked, params: { step: number }
  MARKETING_SKIP_CLICK: "marketing_skip_click", // any time skip to "/me" is clicked, params: { step: number }
  SHOW_RECOMMENDED_COACHES: "show_recommended_coaches", // user is served recommended coach profiles
  MARKETING_INTAKE_FORM: (question: number) =>
    `marketing_intake_form_question_${question}`, // when intake form question is answered, params: { answers: number[] | string, is_tof: boolean }
  MARKETING_INTAKE_FORM_SUBMIT: "marketing_intake_form_submit", // when intake form is submitted, params: { is_tof: boolean }
  MARKETING_VIEW_QUESTIONNAIRE_SUMMARY: "marketing_view_questionnaire_summary", // user views the AI summary from questionnaire
  MARKETING_COACH_READ_MORE_CLICK: "marketing_coach_read_more_click", // user clicks read more for an overflowed coach bio on coach selection page, params: { coach_id: number }
  MARKETING_VIEW_COACH_SUMMARIZER: "marketing_view_coach_summarizer", // user views the AI summaries of coach profiles, params: { coach_id: number }
  MARKETING_VIEW_COACH_VIDEO: "marketing_view_coach_video", // user clicks "Watch coach's 30 sec intro video" on coach selection page, params: { coach_id: number }
  MARKETING_COACH_SELECT: "marketing_coach_select", // when a coach is selected to proceed to next step, params: { coach_id: number }
  MARKETING_POST_SELECTION_SUBMIT: "marketing_post_selection_submit", // when post selection questionnaire is submitted
  MARKETING_CHOOSE_ANOTHER_COACH: "marketing_choose_another_coach", // when user clicks "Choose another coach" from plan select page
  MARKETING_COACH_INITIATE_CHECKOUT: "marketing_coach_initiate_checkout", // initiating checkout flow, params: { bundle_joonity: boolean }
  MARKETING_COACH_PURCHASE_COMPLETE: "marketing_coach_purchase_complete", // when the purchase is completed for coaching, params: { lookup_key: string, bundle_joonity: boolean }
  MARKETING_SECONDARY_OFFERING_MODAL_OPEN:
    "marketing_secondary_offering_modal_open", // when the secondary offering modal is opened (clicking "No thanks" on plan select page)
  MARKETING_COACH_ONBOARDING_COMPLETE: "marketing_coach_onboarding_complete", // when "Let's Go!" on last step is clicked

  //// Quests
  ADD_QUEST: "add_quest", // User adds new quest, params: { is_parent_quest: boolean }
  CREATE_CUSTOM_QUEST: "create_custom_quest", // params: { source: location of the custom quest creation }
  OPEN_EDIT_QUEST: "open_edit_quest", // User opens quest edit modal
  EDIT_QUEST: "edit_quest", // User saves quest edits
  OPEN_QUEST_STATUS_MODAL: "open_quest_status_modal", // User opens quest status/quest actions modal
  VIEW_QUEST_DETAILS: "view_quest_details", // User views quest details
  DELETE_QUEST: "delete_quest", // User deletes quest
  CHANGE_QB_DATE: "change_qb_date", // User changes quest board date
  VERIFY_QUEST: "verify_quest", // User verifies completed quest
  ADD_QUEST_INTENT: "add_quest_intent", // user opens add quest modal
  TEMPLATE_CATEGORY_CLICK: "qb_template_category_click", // user clicks a template category, { category: string }
  TEMPLATE_CLICK: "qb_template_click", // user clicks a template
  RETRY_QUEST: "retry_quest", // User retries failed/rejected quest
  REJECT_QUEST: "reject_quest", // User rejects completed quest
  COMPLETE_MULTIPLE_QUESTS: "complete_multiple_quests", // User bulk completes quests
  SKIP_MULTIPLE_QUESTS: "skip_multiple_quests", // User bulk skips quests
  REJECT_MULTIPLE_QUESTS: "reject_multiple_quests", // User bulk rejects quests
  REORDER_QUESTS: "reorder_quests", // when user opens up the reorder quest screen
  REORDER_QUESTS_SAVE: "reorder_quests_save", // when user saves the new order of quests
  ACTIVATE_QUESTS: "activate_quests", // when user activates quests
  INACTIVATE_QUESTS: "inactivate_quests", // when user inactivates quests

  //// Rewards
  SEND_BONUS_REWARD: "send_bonus_reward", // user opens bonus reward modal
  CUSTOM_REWARD_CREATE_CUSTOM: "custom_reward_create_custom", // user opens add reward modal
  BONUS_REWARD_SENT: "rewards_bonus_reward_sent", // user sends bonus reward
  CREATE_CUSTOM_REWARD: "create_custom_reward", // params: { currency: string } fires when user creates a reward
  EDIT_CUSTOM_REWARD: "edit_custom_reward", // params: { currency: string } fires when user edits a reward
  DELETE_CUSTOM_REWARD: "delete_custom_reward", // user deletes a reward
  PURCHASE_CUSTOM_REWARD: "purchase_custom_reward", // user purchases a reward
  REDEEM_CUSTOM_REWARD: "redeem_custom_reward", // when redeeming custom reward
  DEDUCT_CUSTOM_REWARD_BALANCE: "deduct_custom_reward_balance", // when deducting custom reward balance
  VIEW_CUSTOM_REWARD_DETAILS: "view_custom_reward_details", // when viewing custom reward details
  EMPTY_STATE_CREATE_CUSTOM: "empty_state_create_custom", //  params: { type: string } => "custom_reward", "quest" for quest board. fires when user clicks “Create your first Custom Reward” in custom reward empty state
  EMPTY_STATE_OPEN_TEMPLATES: "empty_state_open_templates", //  params: { type: string } => "custom_reward", "quest" for quest board. fires when user clicks “see what other parents are creating” in custom reward empty state
  OPEN_DEDUCT_COINS: "open_deduct_coins", // user opens deduct coins modal
  SEND_COIN_DEDUCTIONS: "send_coin_deductions", // User deducts coins from a child

  //// Rewards: Screen time controls
  SHOW_SCREEN_TIME_INFORMATIONAL: "show_screen_time_informational", // when the user sees the informational modal in welcome flow
  TOGGLE_ONBOARDING_SCREENTIME: "toggle_onboarding_screentime", // when the user turns on screentime in welcome flow. params: { enabled: boolean }
  REWARD_DETAILS_SETUP_SCREENTIME: "reward_details_setup_screentime", // when user clicks “Set up screen time controls" or "Screen time controls help" in reward details page
  REWARDS_TAB_SETUP_SCREENTIME: "rewards_tab_setup_screentime", // when user clicks Finish set up (2 min)" in rewards tab
  VIEW_SCREENTIME_INSTRUCTIONS: "view_screentime_instructions", // when they click the button to go to the next step on the first step of the "Introducing screen time controls" modal: params: { modal_type: "post_create" | "rewards_tab" | "rewards_details" | "ftux" }
  SCREENTIME_INSTRUCTIONS: "screentime_instructions", // when displaying content in the instructions modal: params: { step: "step_selection" | "setup_family_sharing" | "setup_passcode" | "setup_joonity" }
  SCREENTIME_SETUP_SUCCESS: "screentime_setup_success", // when the user successfully sets up a screen time reward
  VIEW_SCREENTIME_FTUX: "view_screentime_ftux", // when the user sees the FTUX modal
  SCREENTIME_FTUX_CREATE_REWARD: "screentime_ftux_create_reward", // when they click create reward/set up screen time rewards on FTUX
  SCREENTIME_FTUX_DISMISS: "screentime_ftux_dismiss", // when they click dismiss on FTUX modal
  SHOW_SCREENTIME_REMINDER_BUTTON: "show_screentime_reminder_button", // when "Finish set up (2 min)" button surfaces on a reward for the user. params: { reward_id: number }
  SCREENTIME_REMINDER_DISMISS: "screentime_reminder_dismiss", // when they click dismiss on the modal that appears after clicking "Finish set up (2 min)" in rewards tab
  OPEN_AUTO_REDEEM_SETTINGS: "open_auto_redeem_settings", // when the user opens the auto redeem settings modal by clicking on gear icon
  TOGGLE_AUTO_REDEEM: "toggle_auto_redeem", // when the user toggles auto redeem on or off. params: { enabled: boolean }
  SCREENTIME_SETUP_DEVICETYPE: "screentime_setup_devicetype", // when the user selects a device type on the first step of the "Introducing screen time controls" modal: params: { devicetype: "ios" | "android" }
  SCREENTIME_SAVE_SCHEDULED_EXCEPTION: "screentime_save_scheduled_exception", // when the user hits save on the scheduled screen time restrictions modal
  SCREENTIME_CREATE_MANUAL_EXCEPTION: "screentime_create_manual_exception", // user confirms that they want to toggle on a manual exception (toggled off)

  //// Family
  SEND_THERAPIST_INVITE: "send_therapist_invite", // User invites therapist
  MANAGE_THERAPISTS_BUTTON_CLICK: "manage_therapists_button_click", // User clicks manage therapists button
  ADD_THERAPIST_PROMPT_INVITE: "add_therapist_prompt_invite", // User prompted to add therapist
  TOGGLE_DOTER_CHAT: "toggle_doter_chat", // User toggles doter chat { source: 'ftux' | 'settings' | 'flagged_details', enabled: boolean }
  OPEN_DOTER_CHAT_HISTORY: "open_doter_chat_history", // User opens doter chat history
  SHOW_DOTER_CHAT_INFORMATIONAL: "show_doter_chat_informational", // User sees doter chat informational modal {is_ftux: boolean}
  MARK_ALL_FLAGGED_MESSAGES_READ: "mark_all_flagged_messages_read", // User marks all flagged messages as read
  VIEW_FLAGGED_CONVERSATION: "view_flagged_conversation", // User views a flagged conversation

  //// Family mailbox
  VIEW_FAMILY_MAIL: "view_family_mail", // User views specific mail message
  SEND_FAMILY_MAIL: "send_family_mail", // User sends family mail message, params: { has_gift: boolean, has_audio: boolean, has_image: boolean }
  PURCHASE_AND_ADD_TO_MAIL: "purchase_and_add_to_mail", // User clicks "Purchase and add to mail" in gift shop modal
  PURCHASE_AND_GO_TO_INVENTORY: "purchase_and_go_to_inventory", // User clicks "Purchase and go to inventory" in gift shop modal

  //// OTHER:
  NAV_ITEM_CLICK: "nav_item_click", // params: page
  CONTACT_US_CLICK: "contact_us_click", // user clicks contact us button
  EMAIL_CAMPAIGN_CLICKED: "email_campaign_clicked", // user clicks a link in an email campaign
  SET_PHONE_NUMBER: "set_phone_number", // source param is either: post_therapist_link or settings
  SKIP_SET_PHONE_NUMBER: "skip_set_phone_number", // user skips setting phone number
  OPEN_TEXT_MESSAGE_SETTINGS: "open_text_message_settings", // User opens SMS settings

  //// Browser navigation
  PAGE_REFRESH: "page_refresh", // User refreshes page
  PAGE_CLOSE: "page_close", // User closes page
  PAGE_BACK_FORWARD: "page_back_forward", // User navigates away using back or forward button

  //// Me
  OPEN_QUESTS_REVIEW_CATCHUP: "open_quests_review_catchup", // User opens quest review catchup
  OPEN_QUESTS_COMPLETE_CATCHUP: "open_quests_complete_catchup", // User opens quest completion catchup
  CLICK_TODAYS_PROGRESS: "click_todays_progress", // User views today's progress
  SHOW_ABA_THERAPIST_PROMPT: "show_aba_therapist_prompt", // ABA therapist prompt shown to user
  ABA_THERAPIST_FORCE_CLOSE: "aba_therapist_force_close", // User force closes ABA prompt
  INVITE_THERAPIST_EMAIL: "invite_therapist_email", // User invites therapist via email

  //// Parent tasks
  OPEN_PARENT_TASK_RESOURCE: "open_parent_task_resource", // User opens task resource
  PARENT_TASKS_INITIATE_COMPLETE: "parent_tasks_initiate_complete", // User starts task completion
  PARENT_TASKS_FINISH_COMPLETE: "parent_tasks_finish_complete", // User finishes task
  OPEN_PARENT_TASKS_SHEET: "open_parent_tasks_sheet", // User opens tasks sheet

  //// Data tasks
  OPEN_DATA_COLLECTION_TASKS: "open_data_collection_tasks", // User opens data collection tasks
  INITIATE_DATA_COLLECTION_TASK: "initiate_data_collection_task", // User starts data collection task
  SUBMIT_DATA_COLLECTION_TASK: "submit_data_collection_task", // User submits collected data

  //// Incidents
  INITIATE_LOG_INCIDENT: "initiate_log_incident", // User starts incident logging
  ENTER_LOG_INCIDENT_CHILD_SELECTION: "enter_log_incident_child_selection", // User selects child for incident
  ENTER_LOG_INCIDENT_BEHAVIOR_DESCRIPTION:
    "enter_log_incident_behavior_description", // User describes incident behavior
  ENTER_LOG_INCIDENT_STRATEGY_DESCRIPTION:
    "enter_log_incident_strategy_description", // User describes incident strategy
  SUBMIT_INCIDENT_LOG: "submit_incident_log", // User submits incident log
  LOG_ANOTHER_INCIDENT: "log_another_incident", // User starts another incident log

  //// Parent Training
  OPEN_PARENT_TRAINING_DETAILS: "open_parent_training_details", // User views training details
  START_CONTINUE_LEARNING_MODULES: "start_continue_learning_modules", // User starts/continues modules
  OPEN_LEARNING_MODULE: "open_learning_module", // User opens specific module
  COMPLETE_BASIC_LEARNING_ASSIGNMENT: "complete_basic_learning_assignment", // when the user completes PDF/readingtext/video
  COMPLETE_SHORT_ANSWER_LEARNING_ASSIGNMENT:
    "complete_short_answer_learning_assignment", // when the user submits ALL short answer questions
  COMPLETE_MULTIPLE_CHOICE_LEARNING_ASSIGNMENT:
    "complete_multiple_choice_learning_assignment", // when the user submits ALL multiple choice questions
  FINISH_CURRENT_LEARNING_MODULES: "finish_current_learning_modules", // when the user gets to the end of the learning modules and we're dismissing the screen

  //// Coaching
  VIEW_COACHING_MESSAGES: "view_coaching_messages", // user views coaching messages
  JOIN_COACHING_SESSION: "join_coaching_session", // user joins a coaching session
  ADD_SESSION_TOPIC: "add_session_topic", // user clicks "+ Session Topic" from Me tab coach alert section
  VIEW_NEW_COACH_ROUTINE: "view_new_coach_routine", // user clicks on "New routines from Coach [coach name]!" or "# tasks from Coach [coach name]" from Me tab (two diff buttons)
  NEW_ROUTINES_OVERLAY_CLOSED: "new_routines_overlay_closed", // when the user closes the new routines overlay

  INITIATE_UPDATE_COACH_PLAN: "initiate_update_coach_plan", // when the update plan flow is started
  VIEW_NEW_COACH_PLAN: "view_new_coach_plan", // when the slider changes value & a new plan is selected
  CONFIRM_UPDATE_COACH_PLAN: "confirm_update_coach_plan", // when all confirmations are done & the update is successful
  CANCEL_COACH_PLAN_UPDATE: "cancel_coach_plan_update", // when the user cancels the update plan flow
  UNPAUSE_COACHING_SUBSCRIPTION: "unpause_coaching_subscription", // when the user unpauses coach subscription

  SCHEDULE_COACH_SESSION: "schedule_coach_session", // when the user schedules a coach session
  CANCEL_COACH_SESSION: "cancel_coach_session", // when the user cancels a coach session
  RESCHEDULE_COACH_SESSION: "reschedule_coach_session", // when the user reschedules a coach session
  SHOW_BOOKING_POST_PHONE_NUMBER: "show_booking_post_phone_number", // show the booking modal specifically after user adds phone number

  SHOW_COACHING_TOS: "show_coaching_tos", // when the user views the coaching TOS
  COMPLETE_COACHING_TOS: "complete_coaching_tos", // when the user completes the coaching TOS

  // AI Quests
  OPEN_AI_QUEST_BUILDER: "open_ai_quest_builder", // when the user opens the AI quest builder
  CLOSE_AI_QUEST_BUILDER: "close_ai_quest_builder", // when the user closes the AI quest builder
  AI_SEND_QUEST_BUILDER_PROMPT: "ai_send_quest_builder_prompt", // when the user sends the prompt to the AI quest builder
  AI_QUEST_BUILDER_NEW_SESSION: "ai_quest_builder_new_session", // when the user starts a new session of the AI quest builder
  AI_QUEST_BUILDER_RESTART_SESSION: "ai_quest_builder_restart_session", // when the user restarts the session of the AI quest builder
  AI_QUEST_BUILDER_ACCEPT_ALL_CHANGES: "ai_quest_builder_accept_all_changes", // when the user accepts all changes from the AI quest builder
  AI_QUEST_BUILDER_ACCEPT_SELECTED: "ai_quest_builder_accept_selected", // when the user accepts the selected changes from the AI quest builder
  AI_QUEST_BUILDER_REMOVE_CHANGES: "ai_quest_builder_remove_changes", // when the user removes the changes from the AI quest builder
  AI_QUEST_BUILDER_ERROR: "ai_quest_builder_error", // when the AI quest builder encounters an error

  //// Cancel Subscription Flow
  INITIATE_CANCEL_SUB: "initiate_cancel_sub", // when the user clicks “cancel” on manage subscription page, params: { product: "joonity" | "coaching" }
  PAUSE_COACHING_SUBSCRIPTION: "pause_coaching_subscription", // when user pauses, params: { length: 1 | 2 | 3 }
  EXTEND_JOONITY_TRIAL: "extend_joonity_trial", // extending subscription trial
  ACCEPT_CANCEL_FLOW_SPECIAL_OFFER: "accept_cancel_flow_special_offer", // when user accepts special offer, params: { product: "joonity" | "coaching" }
  WHEN_USER_SUBMITS_SURVEY: "when_user_submits_survey", // when user submits survey, params: { answer: string, reason: string }
  CANCEL_SUBSCRIPTION: "cancel_subscription", // actually canceling sub, params: { product: "joonity" | "coaching" }
  DECLINE_CANCEL_OFFER: "decline_cancel_offer", // when they’re skipping an offer we give them, params: { product: "joonity" | "coaching", step: CancelFlowStepType }

  //// CHURNKEY
  CHURNKEY_OPEN: "churnkey_open", // when the user opens the churnkey modal
  CHURNKEY_CLOSE: "churnkey_close", // when the user closes the churnkey modal
  CHURNKEY_CANCEL: "churnkey_cancel", // when the user cancels the churnkey modal
  CHURNKEY_PAUSE: "churnkey_pause", // when the user pauses the churnkey modal
  CHURNKEY_DISCOUNT_CHANGE: "churnkey_discount_change", // when the user changes the discount on the churnkey modal
  CHURNKEY_PLAN_CHANGE: "churnkey_plan_change", // when the user changes the plan on the churnkey modal
  CHURNKEY_STEP_CHANGE: "churnkey_step_change", // when the user changes the step on the churnkey modal
  CHURNKEY_RESUME: "churnkey_resume", // when the user resumes the churnkey modal
}

export enum FacebookPixelEvent {
  CONTACT = "Contact",
  COMPLETE_REGISTRATION = "CompleteRegistration",
  START_TRIAL = "StartTrial",
}

export enum TiktokPixelEvent {
  CONTACT = "Contact",
  COMPLETE_REGISTRATION = "CompleteRegistration",
  START_TRIAL = "PlaceAnOrder",
  PURCHASE_SUBSCRIPTION = "CompletePayment",
}

export enum PinterestPixelEvent {
  CONTACT = "Lead",
  COMPLETE_REGISTRATION = "SignUp",
  START_TRIAL = "Checkout",
}

const isProduction = import.meta.env.MODE === "production"
// Initialize amplitude
isProduction &&
  init(import.meta.env.VITE_AMPLITUDE_API_KEY as string, undefined, {
    minIdLength: 1,
  })
// Initialize hotjar
// isProduction && Hotjar.init(3623886, 6)

export function analyticsUserDidLogin(
  parentId: number,
  familyId: number,
  email: string
) {
  // User id (as string)
  setUserId(`${parentId}`)

  // User properties
  const event = new Identify()
  event.set("family_id", `${familyId}`)
  event.set("email", email)
  localStorage.setItem(localStorageItems.analyticsFamilyId, `${familyId}`)
  localStorage.setItem(localStorageItems.analyticsUserId, `${parentId}`)
  identify(event)

  // Set family group
  setGroup("family", `${familyId}`)
}

export function analyticsUserDidLogout() {
  reset()
  localStorage.removeItem(localStorageItems.analyticsFamilyId)
  localStorage.removeItem(localStorageItems.analyticsUserId)
}

export function setCountryUserProperty(country: string) {
  const event = new Identify()
  event.set("country", country)
  identify(event)
}

export function trackAnalyticEvent(
  eventName: string,
  eventParams: { [key: string]: any } = {}
) {
  if (!isProduction) return
  eventParams.platform = "web"
  eventParams.family_id = localStorage.getItem(
    localStorageItems.analyticsFamilyId
  )
  eventParams.user_id = localStorage.getItem(localStorageItems.analyticsUserId)

  track(eventName, eventParams)
}

export enum GAEvent {
  START_QUIZ = "AW-343454768/kDV8CILi5dQDELDo4qMB",
  CREATE_ACCOUNT = "AW-343454768/QAqSCPrN5dQDELDo4qMB",
  START_TRIAL = "AW-343454768/-_y7CLiBtqYYELDo4qMB",
  COACH_PURCHASE = "AW-343454768/lykuCMO7-cMbELDo4qMB",
  PURCHASE_COMPLETED = "AW-343454768/Nop5CLndlNUDELDo4qMB",
  // Medium-intent signal: parent finished the wheel-of-life quiz but hasn't
  // yet added child info. Used to train Google Ads bidding on quiz-completers.
  // Configured in Google Ads as the "Complete Quiz" conversion action with a
  // default value of $5 USD — value/currency are sent from trackGAEvent below
  // so they match the conversion-action's expected payload.
  COMPLETED_QUIZ = "AW-343454768/Kaq7COC8jawcELDo4qMB",
}

// User-provided data forwarded to Google Ads for Enhanced Conversions.
// Google hashes these client-side via SHA-256 inside gtag.js before sending,
// so we can pass plaintext here. Only the fields we actually have are sent —
// empty strings degrade match quality.
export interface EnhancedConversionUserData {
  email?: string | null
  /** E.164 format e.g. "+15551234567" */
  phone?: string | null
  firstName?: string | null
  lastName?: string | null
}

// Map of GAEvent send_to → semantic event name for the GTM dataLayer push
// below. GTM tags can be set to trigger on these custom event names in any
// environment (preview + prod), so QA can verify a conversion will fire by
// watching the dataLayer in GTM Preview Mode — without us actually firing a
// Google Ads conversion from the preview URL.
const GA_EVENT_TO_DATALAYER_NAME: Record<string, string> = {
  "AW-343454768/kDV8CILi5dQDELDo4qMB": "start_quiz",
  "AW-343454768/QAqSCPrN5dQDELDo4qMB": "create_account",
  "AW-343454768/-_y7CLiBtqYYELDo4qMB": "start_trial",
  "AW-343454768/lykuCMO7-cMbELDo4qMB": "coach_purchase",
  "AW-343454768/Nop5CLndlNUDELDo4qMB": "purchase_completed",
  "AW-343454768/Kaq7COC8jawcELDo4qMB": "completed_quiz",
}

export function trackGAEvent(
  gaEvent: string,
  data?: any,
  userData?: EnhancedConversionUserData
) {
  // Per-SESSION dedup (was per-browser-forever via localStorage). Stops a
  // single tab from double-firing the same conversion within one visit, but
  // resets on tab close so a returning user — who clicks a different ad
  // weeks later — still gets attributed. Google Ads' own conversion-counting
  // settings ("Count: One" vs "Every") handle the cross-session dedup at
  // the campaign level, so we don't need to enforce it client-side.
  //
  // Dedup applies to BOTH the dataLayer push and the real conversion so QA
  // sees realistic single-fire behavior in GTM Preview Mode.
  if (sessionStorage.getItem(gaEvent)) return
  sessionStorage.setItem(gaEvent, "true")

  // GTM-visible dataLayer push — fires on every environment (preview + prod)
  // so QA can verify a conversion is wired up correctly via GTM Preview Mode
  // without us actually sending traffic to Google Ads from a preview URL.
  // The named event (e.g. "completed_quiz") is what GTM triggers should
  // listen for; tags that send the AW conversion are still gated below.
  const dataLayerEventName = GA_EVENT_TO_DATALAYER_NAME[gaEvent]
  if (dataLayerEventName && typeof window !== "undefined") {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: dataLayerEventName,
      send_to: gaEvent,
      ...(data || {}),
    })
  }

  // Real Google Ads conversion only fires in prod (not on previews / dev) —
  // previews share the same gtag account, and a stray preview-side fire
  // poisons bidding signals.
  if (import.meta.env.DEV || isVercelDeployment) return
  if (typeof gtag === "undefined") return

  // Enhanced Conversions: register user-provided data BEFORE the conversion
  // event so Google Ads can match this conversion to the originating ad
  // click even when third-party cookies are blocked (Safari ITP, etc.).
  // Was previously only happening via the GTM container's AW-343454768 tag
  // (which we paused to remove the 2x duplicate fire); this lifts the same
  // enrichment into the on-page gtag.js path so we don't lose it.
  if (userData) {
    const ud: Record<string, unknown> = {}
    if (userData.email) ud.email = userData.email
    if (userData.phone) ud.phone_number = userData.phone
    const address: Record<string, string> = {}
    if (userData.firstName) address.first_name = userData.firstName
    if (userData.lastName) address.last_name = userData.lastName
    if (Object.keys(address).length > 0) ud.address = address
    if (Object.keys(ud).length > 0) gtag("set", "user_data", ud)
  }

  gtag("event", "conversion", { send_to: gaEvent, ...data })
}
